import type { MessageView } from "@walkie/protocol"
import { deliveryState } from "../lib/delivery"
import { isGithub } from "../lib/messages"
import type { Filter, State } from "./store"

/** Root messages shown in the open room under the current filter, oldest first. */
export function visibleRoots(s: State): MessageView[] {
	const { roomId, filter, authorFilter } = s.ui
	if (!roomId) return []
	const meId = s.me?.user.id
	const meHandle = s.me?.user.handle
	const mine = new Set(
		Object.values(s.agents)
			.filter(a => a.userId === meId)
			.map(a => a.id),
	)
	return Object.values(s.messages)
		.filter(m => m.roomId === roomId && !m.threadId)
		.filter(m => !s.hideGithub || !isGithub(m))
		.filter(
			m =>
				!authorFilter ||
				m.author.id === authorFilter ||
				m.toAgents.includes(authorFilter),
		)
		.filter(m => matches(m, filter, s.now, meId, meHandle, mine))
		.sort((a, b) => a.seq - b.seq)
}

function matches(
	m: MessageView,
	f: Filter,
	now: number,
	meId: string | undefined,
	meHandle: string | undefined,
	mine: Set<string>,
): boolean {
	switch (f) {
		case "all":
			return true
		case "requests":
			return !!m.request
		case "blockers":
			return m.kind === "blocker"
		case "failing":
			return (m.deliveries ?? []).some(d => deliveryState(d, now) === "failed")
		case "mine":
			return (
				(!!meId && (m.toUsers.includes(meId) || m.author.id === meId)) ||
				m.toAgents.some(a => mine.has(a)) ||
				(!!m.author.id && mine.has(m.author.id)) ||
				(!!meHandle && new RegExp(`@${meHandle}\\b`, "i").test(m.body)) ||
				(!!m.request &&
					((!!m.request.ownerUserId && m.request.ownerUserId === meId) ||
						(!!m.request.ownerAgentId && mine.has(m.request.ownerAgentId))))
			)
	}
}

/** GitHub-authored root messages in the open room that the toggle is hiding. */
export function hiddenGithubCount(s: State): number {
	const { roomId } = s.ui
	if (!s.hideGithub || !roomId) return 0
	let n = 0
	for (const m of Object.values(s.messages))
		if (m.roomId === roomId && !m.threadId && isGithub(m)) n++
	return n
}

export function threadMessages(s: State, rootId: string): MessageView[] {
	return Object.values(s.messages)
		.filter(m => m.threadId === rootId)
		.sort((a, b) => a.seq - b.seq)
}
